import { FC, useMemo } from 'react';

import { useChannels } from '@mint-lib/hooks';
import { useTranslation } from '@mint-lib/i18n';
import { snakeCase } from 'snake-case';

import {
  Chip,
  Icon,
  Table,
  TableBody,
  TableBodyCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@myn/mui';

import ConditionalCurrencyField from '../../../atoms/ConditionalCurrencyField.jsx';
import { useCampaignDataSelector } from '../../../hooks/useGetCampaignData.js';
import BudgetDifference from './BudgetDifference.jsx';

type Props = {
  channels: Record<
    string,
    {
      difference: string | null;
      currentPercent: string | null;
      current: string | null;
      previous: string | null;
    }
  >;
};

const ChannelDifferenceTable: FC<Props> = ({ channels }) => {
  const { t } = useTranslation('@myn/planning');
  const { data: currency } = useCampaignDataSelector('currency');
  const channelsMeta = useChannels();
  const newChannels = useMemo(() => {
    return Object.entries(channels).map(([channelName, channelData]) => {
      const preparedName = snakeCase(channelName);
      const channelMeta = channelsMeta[preparedName];

      return {
        label: channelMeta?.name ?? channelName,
        icon: channelMeta?.icon ?? 'Settings',
        values: {
          ...channelData,
          difference: channelData.difference
            ? Number(channelData.difference.replace('%', ''))
            : 0,
        },
      };
    });
  }, [channels]);

  return (
    <TableContainer
      sx={{
        maxHeight: 272,
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableHeaderCell>{t('Channel')}</TableHeaderCell>
            <TableHeaderCell sx={{ justifyItems: 'flex-end' }}>
              {t('Allocated budget')}
            </TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {newChannels.map((channel) => (
            <TableRow key={channel.label}>
              <TableBodyCell>
                <Icon component={channel.icon} />
                {channel.label}
                <Chip
                  variant="outlined"
                  label={channel.values.currentPercent}
                />
              </TableBodyCell>
              <TableBodyCell align="right">
                {channel.values.difference ? (
                  <BudgetDifference
                    currentBudget={channel.values.current ?? 0}
                    previousBudget={channel.values.previous ?? 0}
                    differencePercent={channel.values.difference}
                    currency={currency}
                  />
                ) : null}
                <ConditionalCurrencyField
                  value={channel.values.current ?? 0}
                  currency={currency}
                />
              </TableBodyCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ChannelDifferenceTable;
